import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import useLocale from '../contexts/LocaleContext';
import { questionnairesApi } from '../lib/api-client';
import type { AnswerDto, QuestionDto, QuestionnaireDocumentDto, QuestionnaireDto } from '../types/api';
import FullScreenPdfViewer from './FullScreenPdfViewer';

interface QuestionnaireFormProps {
  questionnaire: QuestionnaireDto | null;
  onAnswersChange: (answers: AnswerDto[]) => void;
  initialAnswers?: AnswerDto[];
  className?: string;
  emptyStateMessage?: string;
}

const QuestionnaireForm: React.FC<QuestionnaireFormProps> = ({
  questionnaire,
  onAnswersChange,
  initialAnswers = [],
  className = '',
  emptyStateMessage,
}) => {
  const { locale, dictionary } = useLocale();
  const [questions, setQuestions] = useState<QuestionDto[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [subquestions, setSubquestions] = useState<Record<string, QuestionDto[]>>({});
  const [loadingSubquestions, setLoadingSubquestions] = useState<Record<string, boolean>>({});
  const [referenceDocuments, setReferenceDocuments] = useState<QuestionnaireDocumentDto[]>([]);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);

  useEffect(() => {
    if (!questionnaire?.id) {
      setReferenceDocuments([]);
      return;
    }
    let cancelled = false;
    questionnairesApi
      .getReferenceDocuments(questionnaire.id)
      .then(docs => { if (!cancelled) setReferenceDocuments(docs); })
      .catch(error => console.error('Error fetching reference documents:', error));
    return () => { cancelled = true; };
  }, [questionnaire?.id]);

  // Track previous initialAnswers to avoid unnecessary updates
  const prevInitialAnswersRef = React.useRef<AnswerDto[]>([]);
  const prevInitialAnswersStringRef = React.useRef<string>('');

  const transformQuestion = useCallback((q: QuestionDto): QuestionDto => {
    return {
      ...q,
      options:
        q.facilityAssessmentQuestionOptions?.map(opt => ({
          ...opt,
          text: opt.option,
          score: opt.marks,
        })) || q.options || [],
    };
  }, []);

  const isQuestionVisible = useCallback((question: QuestionDto, selectedOptionIds: Set<string>) => {
    if (!question.skipIfOptionId) return true;
    return !selectedOptionIds.has(question.skipIfOptionId);
  }, []);

  const buildFilteredAnswerDtos = useCallback(
    (answerMap: Record<string, string>): AnswerDto[] => {
      const selectedOptionIds = new Set(Object.values(answerMap).filter(Boolean));
      const visibleIds = new Set<string>();
      // IDs of every question we can currently classify: main questions plus all
      // sub-questions whose defs have been fetched. Anything outside this set is
      // "unknown" (its parent option's sub-question fetch hasn't resolved yet) and
      // must be KEPT — dropping it here is what used to delete sub-answers.
      const knownIds = new Set<string>(questions.map(q => q.id));
      Object.values(subquestions).forEach(subs => subs.forEach(subQ => knownIds.add(subQ.id)));

      const visibleMain = questions.filter(q => !q.parentQuestionId && isQuestionVisible(q, selectedOptionIds));

      visibleMain.forEach(question => {
        visibleIds.add(question.id);
        const selectedOptionId = answerMap[question.id];
        if (!selectedOptionId) return;
        const subs = subquestions[selectedOptionId] || [];
        subs.forEach(subQ => {
          if (isQuestionVisible(subQ, selectedOptionIds)) {
            visibleIds.add(subQ.id);
          }
        });
      });

      // Are there selected options whose sub-questions we haven't checked yet?
      // While any check is pending we cannot prove an unknown answer is stale.
      const hasPendingSubquestionChecks = Object.values(answerMap).some(
        oid => Boolean(oid) && subquestions[oid] === undefined
      );

      return Object.entries(answerMap)
        .filter(
          ([qid, oid]) =>
            Boolean(oid) && (visibleIds.has(qid) || (!knownIds.has(qid) && hasPendingSubquestionChecks))
        )
        .map(([questionId, selectedOptionId]) => ({ questionId, selectedOptionId }));
    },
    [isQuestionVisible, questions, subquestions]
  );

  useEffect(() => {
    // Use JSON.stringify for comparison but store it in a ref to avoid recreating on every render
    const currentAnswersString = JSON.stringify(initialAnswers);

    // Only update if the stringified version changed
    if (currentAnswersString !== prevInitialAnswersStringRef.current && initialAnswers && initialAnswers.length > 0) {
      const initialAnswersMap: Record<string, string> = {};
      initialAnswers.forEach(answer => {
        initialAnswersMap[answer.questionId] = answer.selectedOptionId;
      });
      setAnswers(initialAnswersMap);
      prevInitialAnswersRef.current = initialAnswers;
      prevInitialAnswersStringRef.current = currentAnswersString;

      // Fetch subquestions for all initial answers — in parallel, and store empty results
      // too so the answer-pruning effect can tell "checked, none exist" from "not yet
      // checked" (an unchecked option means sub-answers must be kept, never dropped).
      const fetchAllSubquestions = async () => {
        const optionIds = [...new Set(initialAnswers.map(answer => answer.selectedOptionId))];

        await Promise.all(
          optionIds.map(async optionId => {
            try {
              const fetchedSubquestions = await questionnairesApi.getSubquestionsByOption(optionId);
              const transformedSubquestions = (fetchedSubquestions ?? []).map(transformQuestion);
              setSubquestions(prev =>
                prev[optionId] !== undefined ? prev : { ...prev, [optionId]: transformedSubquestions }
              );
            } catch (error) {
              // Leave this option unchecked — pruning stays disabled for it, so no answers are lost.
              console.error('Error fetching subquestions for option:', optionId, error);
            }
          })
        );
      };

      fetchAllSubquestions();
    }
  }, [initialAnswers]);

  useEffect(() => {
    if (questionnaire && questionnaire.questions) {
      // Transform the API data to match our expected format
      const transformedQuestions = questionnaire.questions
        .map(transformQuestion)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      setQuestions(transformedQuestions);

      // Seed sub-question definitions embedded in the questionnaire payload (grouped by
      // their trigger option). This makes sub-questions known synchronously on mount, so
      // restored/back-navigated sub-answers are never pruned while a fetch is in flight.
      const seeded: Record<string, QuestionDto[]> = {};
      questionnaire.questions.forEach(q => {
        (q.subQuestions ?? []).forEach(sub => {
          if (!sub?.triggerOptionId) return;
          const key = String(sub.triggerOptionId);
          if (!seeded[key]) seeded[key] = [];
          seeded[key].push(transformQuestion(sub));
        });
      });
      if (Object.keys(seeded).length > 0) {
        // Fetched entries win over seeded ones (they are at least as fresh).
        setSubquestions(prev => ({ ...seeded, ...prev }));
      }

      if (initialAnswers.length === 0) {
        setAnswers({});
      }
    } else {
      setQuestions([]);
      if (initialAnswers.length === 0) {
        setAnswers({});
      }
    }
  }, [questionnaire?.id, initialAnswers.length, transformQuestion]);

  const handleAnswerChange = useCallback(
    async (questionId: string, optionId: string) => {
      const previousOptionId = answers[questionId];
      const newAnswers = { ...answers, [questionId]: optionId };

      // If parent option changed, remove answers from old visible branch.
      if (previousOptionId && previousOptionId !== optionId) {
        const previousSubs = subquestions[previousOptionId] || [];
        previousSubs.forEach(subQ => {
          delete newAnswers[subQ.id];
        });
      }
      setAnswers(newAnswers);
      // Propagate immediately — the parent must never wait on a sub-question fetch.
      onAnswersChange(buildFilteredAnswerDtos(newAnswers));

      // Fetch subquestions if this option hasn't been checked yet (empty results are
      // stored too, marking the option as checked).
      if (subquestions[optionId] === undefined) {
        setLoadingSubquestions(prev => ({ ...prev, [optionId]: true }));
        try {
          const fetchedSubquestions = await questionnairesApi.getSubquestionsByOption(optionId);
          const transformedSubquestions = (fetchedSubquestions ?? []).map(transformQuestion);
          setSubquestions(prev => ({ ...prev, [optionId]: transformedSubquestions }));
        } catch (error) {
          console.error('Error fetching subquestions:', error);
        } finally {
          setLoadingSubquestions(prev => ({ ...prev, [optionId]: false }));
        }
      }
    },
    [answers, onAnswersChange, subquestions, transformQuestion, buildFilteredAnswerDtos]
  );

  useEffect(() => {
    const filtered = buildFilteredAnswerDtos(answers);
    const filteredMap = Object.fromEntries(filtered.map(item => [item.questionId, item.selectedOptionId]));
    const changed =
      Object.keys(filteredMap).length !== Object.keys(answers).length ||
      Object.keys(answers).some(key => answers[key] !== filteredMap[key]);

    if (changed) {
      setAnswers(filteredMap);
      onAnswersChange(filtered);
    }
  }, [answers, buildFilteredAnswerDtos, onAnswersChange]);

  const selectedOptionIds = useMemo(() => new Set(Object.values(answers).filter(Boolean)), [answers]);
  const visibleMainQuestions = useMemo(
    () => questions.filter(q => !q.parentQuestionId && isQuestionVisible(q, selectedOptionIds)),
    [questions, isQuestionVisible, selectedOptionIds]
  );

  const { totalScore, maxScore, percentage } = useMemo(() => {
    let totalScore = 0;
    let maxScore = 0;

    // Calculate for main questions
    visibleMainQuestions.forEach(question => {
      maxScore += question.weight;
      const selectedOptionId = answers[question.id];

      if (selectedOptionId && question.options && question.options.length > 0) {
        const selectedOption = question.options.find(opt => opt.id === selectedOptionId);
        if (selectedOption) {
          totalScore += selectedOption.score;
        }

        // Calculate for subquestions
        const subs = subquestions[selectedOptionId] || [];
        subs.forEach(subQ => {
          if (!isQuestionVisible(subQ, selectedOptionIds)) return;
          maxScore += subQ.weight;
          const subAnswerId = answers[subQ.id];
          if (subAnswerId && subQ.options && subQ.options.length > 0) {
            const subOption = subQ.options.find(opt => opt.id === subAnswerId);
            if (subOption) {
              totalScore += subOption.score;
            }
          }
        });
      }
    });

    return { totalScore, maxScore, percentage: maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0 };
  }, [visibleMainQuestions, answers, subquestions, isQuestionVisible, selectedOptionIds]);

  // Calculate total questions (main + visible subquestions) and answered count
  const { totalQuestions, answeredQuestionsCount } = useMemo(() => {
    let total = visibleMainQuestions.length;
    let answered = 0;

    // Count answered main questions
    visibleMainQuestions.forEach(question => {
      if (answers[question.id]) {
        answered++;

        // Add subquestions to total if they exist for this answer
        const selectedOptionId = answers[question.id];
        const subs = subquestions[selectedOptionId] || [];
        const visibleSubs = subs.filter(subQ => isQuestionVisible(subQ, selectedOptionIds));
        total += visibleSubs.length;

        // Count answered subquestions
        visibleSubs.forEach(subQ => {
          if (answers[subQ.id]) {
            answered++;
          }
        });
      }
    });

    return { totalQuestions: total, answeredQuestionsCount: answered };
  }, [visibleMainQuestions, answers, subquestions, isQuestionVisible, selectedOptionIds]);

  const sectionOrder = useMemo(() => {
    return [...(questionnaire?.sections || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [questionnaire?.sections]);

  const groupedMainQuestions = useMemo(() => {
    const grouped: Record<string, QuestionDto[]> = {};
    visibleMainQuestions.forEach(question => {
      const key = question.sectionId || '__unsectioned__';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(question);
    });
    Object.keys(grouped).forEach(key => {
      grouped[key] = grouped[key].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    });
    return grouped;
  }, [visibleMainQuestions]);

  const renderQuestionCard = (question: QuestionDto, questionIndex: number) => (
    <div key={question.id} className='border border-gray-200 rounded-lg p-4'>
      <div className='mb-4'>
        <p className='text-xs font-semibold text-primary mb-1 uppercase tracking-wide'>
          {dictionary['questionnaire.question']} {questionIndex + 1}
        </p>
        <p className='text-gray-800 font-medium'>
          {locale === 'rw'
            ? question.questionKinyarwanda
            : locale === 'fr'
              ? question.questionFrench
              : question.question}
        </p>
      </div>

      <div className='space-y-2'>
        {question.options && question.options.length > 0 ? (
          question.options.map(option => (
            <div key={option.id}>
              <label
                className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                  answers[question.id] === option.id
                    ? 'border-primary bg-secondary'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <input
                  type='radio'
                  name={`question-${question.id}`}
                  value={option.id}
                  checked={answers[question.id] === option.id}
                  onChange={() => handleAnswerChange(question.id, option.id)}
                  className='mr-3 text-primary focus:ring-primary'
                />
                <div className='flex-1'>
                  <span className='text-gray-900'>
                    {locale === 'rw'
                      ? option.optionKinyarwanda
                      : locale === 'fr'
                        ? option.optionFrench
                        : option.option}
                  </span>
                </div>
                <span className='text-sm text-gray-500 ml-2'>
                  ({option.marks}/{question.weight} pts)
                </span>
              </label>

              {answers[question.id] === option.id && (
                <div className='mt-3 ml-8 space-y-3'>
                  {loadingSubquestions[option.id] && (
                    <div className='flex items-center gap-2 p-3 bg-secondary border border-primary/30 rounded-lg'>
                      <Loader2 className='h-4 w-4 animate-spin text-primary' />
                      <span className='text-sm text-primary'>Loading follow-up questions...</span>
                    </div>
                  )}
                  {!loadingSubquestions[option.id] &&
                    subquestions[option.id]
                      ?.filter(subQ => isQuestionVisible(subQ, selectedOptionIds))
                      .map((subQ, subIndex) => (
                        <div key={subQ.id} className='bg-secondary/50 border border-primary/30 rounded-lg p-3'>
                          <div className='mb-2'>
                            <div className='flex items-center gap-2 mb-1'>
                              <span className='w-5 h-5 bg-primary text-white rounded-full flex items-center justify-center text-xs font-semibold'>
                                {subIndex + 1}
                              </span>
                              <p className='text-sm font-medium text-gray-700'>
                                {locale === 'rw'
                                  ? subQ.questionKinyarwanda
                                  : locale === 'fr'
                                    ? subQ.questionFrench
                                    : subQ.question}
                              </p>
                            </div>
                          </div>
                          <div className='space-y-1.5'>
                            {subQ.options && subQ.options.length > 0 ? (
                              subQ.options.map(subOpt => (
                                <label
                                  key={subOpt.id}
                                  className={`flex items-center p-2 border rounded cursor-pointer transition-colors text-sm ${
                                    answers[subQ.id] === subOpt.id
                                      ? 'border-primary bg-white'
                                      : 'border-gray-200 hover:border-primary/40 hover:bg-white/50'
                                  }`}
                                >
                                  <input
                                    type='radio'
                                    name={`subquestion-${subQ.id}`}
                                    value={subOpt.id}
                                    checked={answers[subQ.id] === subOpt.id}
                                    onChange={() => handleAnswerChange(subQ.id, subOpt.id)}
                                    className='mr-2 text-primary focus:ring-primary'
                                  />
                                  <span className='text-gray-900 flex-1'>
                                    {locale === 'rw'
                                      ? subOpt.optionKinyarwanda
                                      : locale === 'fr'
                                        ? subOpt.optionFrench
                                        : subOpt.option}
                                  </span>
                                  <span className='text-xs text-gray-500 ml-2'>
                                    ({subOpt.marks}/{subQ.weight} pts)
                                  </span>
                                </label>
                              ))
                            ) : (
                              <div className='text-gray-500 text-xs p-2 border border-gray-200 rounded'>
                                No options available
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className='text-gray-500 text-sm p-3 border border-gray-200 rounded-lg'>
            {dictionary['questionnaire.no_options']}
          </div>
        )}
      </div>
    </div>
  );

  if (!questionnaire) {
    return (
      <div className={`p-4 bg-gray-50 rounded-lg ${className}`}>
        <p className='text-gray-500 text-center'>{emptyStateMessage || dictionary['questionnaire.select_facility']}</p>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className={`p-4 bg-yellow-50 border border-yellow-200 rounded-lg ${className}`}>
        <p className='text-yellow-800 text-center'>{dictionary['questionnaire.no_questions']}</p>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Score Display */}
      <div className='bg-secondary border border-primary/30 rounded-lg p-4'>
        <div className='flex items-center justify-between'>
          <div>
            <h3 className='text-lg font-semibold text-primary'>{dictionary['questionnaire.progress']}</h3>
            <p className='text-sm text-primary/80'>
              {answeredQuestionsCount} {dictionary['questionnaire.answered']} {totalQuestions}{' '}
              {dictionary['questionnaire.questions_answered']}
            </p>
          </div>
          <div className='text-right'>
            <div className='text-2xl font-bold text-primary'>{percentage}%</div>
            <div className='text-sm text-primary/80'>
              {totalScore} / {maxScore} {dictionary['questionnaire.points']}
            </div>
          </div>
        </div>
        <div className='mt-2 w-full bg-primary/20 rounded-full h-2'>
          <div
            className='bg-primary h-2 rounded-full transition-all duration-300'
            style={{ width: `${percentage}%` }}
          />
        </div>
        {referenceDocuments.length > 0 && (
          <button
            type='button'
            onClick={() => setShowDocumentViewer(true)}
            className='mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline'
          >
            <FileText className='h-4 w-4' /> {dictionary['questionnaire.view_documents']}
          </button>
        )}
      </div>

      <FullScreenPdfViewer
        open={showDocumentViewer}
        onOpenChange={setShowDocumentViewer}
        documents={referenceDocuments}
        title={dictionary['questionnaire.reference_documents']}
      />

      {/* Questions */}
      <div className='space-y-8'>
        {sectionOrder.map(section => {
          const sectionQuestions = groupedMainQuestions[section.id] || [];
          if (sectionQuestions.length === 0) return null;
          return (
            <div key={section.id} className='space-y-4'>
              <h3 className='text-base font-semibold text-gray-800 border-b-2 border-primary/20 pb-2'>
                {locale === 'rw'
                  ? section.titleKinyarwanda || section.title
                  : locale === 'fr'
                    ? section.titleFrench || section.title
                    : section.title}
              </h3>
              {sectionQuestions.map(renderQuestionCard)}
            </div>
          );
        })}
        {(groupedMainQuestions.__unsectioned__ || []).map(renderQuestionCard)}
      </div>

      {/* Completion Status */}
      {answeredQuestionsCount === totalQuestions && totalQuestions > 0 && (
        <div className='bg-green-50 border border-green-200 rounded-lg p-4'>
          <div className='flex items-center'>
            <div className='flex-shrink-0'>
              <svg className='h-5 w-5 text-green-400' viewBox='0 0 20 20' fill='currentColor'>
                <path
                  fillRule='evenodd'
                  d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <div className='ml-3'>
              <h3 className='text-sm font-medium text-green-800'>{dictionary['questionnaire.complete']}</h3>
              <p className='text-sm text-green-700'>
                {dictionary['questionnaire.all_answered']} {percentage}%
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(QuestionnaireForm);
